import {
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  ElementRef,
  HostListener,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AppointmentService } from '../../services/appointment.service';
import { Appointment } from '../../models/appointment.model';
import { RescheduleAppointmentModalComponent } from '../reschedule-appointment-modal/reschedule-appointment-modal.component';
import { CancelAppointmentModalComponent } from '../cancel-appointment-modal/cancel-appointment-modal.component';
import { CancellationReasonModalComponent } from '../cancellation-reason-modal/cancellation-reason-modal.component';
import { CancellationSuccessModalComponent } from '../cancellation-success-modal/cancellation-success-modal.component';
import { AppointmentDetailsModalComponent } from '../appointment-details-modal/appointment-details-modal.component';
import { FilterOptions } from '../appointments-filter-modal/appointments-filter-modal.component';
import { ProfileService } from '../../services/profile.service';
import { PackageService } from '../../services/package.service';
import { ApiService } from '../../services/api.service';
import { RoleService } from '../../services/role.service';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { formatDuration, runRescheduleUpdate } from '../../shared/utils/appointment.util';
import { mapEmployeeIdsToNames } from '../../shared/utils/appointment-filter.util';
import { getUserTimezone } from '../../shared/utils/date.util';

interface AppointmentEvent {
  id: string;
  title: string;
  date: string;
  status: 'confirmed' | 'rescheduled' | 'cancelled' | 'pending' | 'completed';
  time?: string;
  aptNumber: string;
  appointmentId?: string;
  customerName?: string;
  packageName?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleYear?: string;
  employeeName?: string;
  appointmentTime?: string;
  duration?: { minutes?: number; hours?: number };
}

@Component({
  selector: 'app-appointments-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FullCalendarModule,
    MatDatepickerModule,
    RescheduleAppointmentModalComponent,
    CancelAppointmentModalComponent,
    CancellationReasonModalComponent,
    CancellationSuccessModalComponent,
    AppointmentDetailsModalComponent,
  ],
  templateUrl: './appointments-calendar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./appointments-calendar.component.scss'],
})
export class AppointmentsCalendarComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
  @ViewChild('headerElement') headerElement!: ElementRef;
  @ViewChild('datePickerDropdown') datePickerDropdown!: ElementRef;
  @ViewChild('calendarBody') calendarBody!: ElementRef;

  @Input() searchTerm = '';
  @Input() selectedLocation = '';
  @Input() selectedLocationLabels: string[] = [];
  @Input() activeFilters: FilterOptions | null = null;
  @Output() createAnotherAppointment = new EventEmitter<void>();
  @Input() isAdminUser = false;

  currentDate = new Date(); // February 2025 to show sample appointments
  currentView: 'day' | 'week' | 'month' = 'month';
  weekType: 'full' | 'work' = 'full'; // New property to track week type
  isLoading = true;

  appointments: AppointmentEvent[] = [];
  private originalAppointments: Appointment[] = []; // Store original appointment data
  private tooltipHideTimeout: any;
  private dragTooltipHideTimeout: any;
  private isDragging = false;
  private draggedEvent: any = null;
  private lastMouseX = 0;
  private lastMouseY = 0;

  // Modal properties
  isRescheduleModalVisible = false;
  isCancelModalVisible = false;
  isCancellationReasonModalVisible = false;
  isCancellationSuccessModalVisible = false;
  isCancellationErrorModalVisible = false;
  isAppointmentDetailsModalVisible = false;
  appointmentToReschedule: Appointment | null = null;
  appointmentToCancel: Appointment | null = null;
  selectedAppointmentForModal: Appointment | null = null;
  droppedDate: Date | null = null;
  isRescheduleLoading = false;

  // Date picker properties
  showDatePicker = false;
  selectedDate: Date | Date[] | null = null;
  datePickerView: 'date' | 'month' | 'year' = 'date';
  minDate: Date = new Date();
  maxDate: Date = new Date();
  selectionMode: 'single' | 'range' | 'multiple' = 'single';

  // More events functionality
  expandedDays = new Set<string>();
  maxVisibleEvents = 3;

  // CWS-587: the calendar must fetch the range it actually renders. `visibleRange` is the
  // range FullCalendar last reported through `datesSet`; `fetchedRange` is the padded window
  // the last API call asked for. The old code fetched a fixed ±1 month around `currentDate`
  // and only refetched in month view, so appointments the list showed (the Upcoming tab has
  // no end bound) were never requested by the calendar.
  private visibleRange: { start: Date; end: Date } | null = null;
  private fetchedRange: { start: string; end: string } | null = null;
  /** Padding either side of the rendered range, so day/week stepping rarely refetches. */
  private readonly fetchRangePaddingDays = 31;

  // Infinite scroll functionality
  private isScrollingToNext = false;
  private scrollThreshold = 50; // pixels from bottom to trigger next month
  private scrollCooldown = 1000; // milliseconds to prevent rapid navigation

  // Subscription management
  private locationsSubscription: Subscription = new Subscription();

  constructor(
    private appointmentService: AppointmentService,
    private profileService: ProfileService,
    private packageService: PackageService,
    private apiService: ApiService,
    private messageService: NotificationService,
    private roleService: RoleService,
    // Angular 22 runs this app zoneless: state set inside an HTTP subscribe callback
    // (isLoading, appointment data, modal-visible flags) never triggers a repaint on
    // its own. Found via automated regression testing — the Calendar view stayed on
    // "Loading appointments..." forever after the API request had already resolved.
    private cdr: ChangeDetectorRef
  ) {}

  // Check if current user is staff (hide reschedule/cancel options for staff)
  get isStaffUser(): boolean {
    return this.roleService.isShopFloorEmployee();
  }

  // Method to toggle expanded state for a day
  /**
   * Toggle the expanded/collapsed state for a given calendar day and refresh calendar events.
   * @example
   * oggleDayExpansion('2026-03-17')
   * undefined
   * @param {string} dateStr - Date string identifying the day to toggle (e.g., 'YYYY-MM-DD').
   * @returns {void} No return value; the function updates component state and refreshes events.
   **/
  toggleDayExpansion(dateStr: string): void {
    if (this.expandedDays.has(dateStr)) {
      this.expandedDays.delete(dateStr);
    } else {
      this.expandedDays.add(dateStr);
    }

    // Refresh calendar events to update the display
    this.refreshCalendarEvents();
  }

  // Method to check if a day is expanded
  isDayExpanded(dateStr: string): boolean {
    const isExpanded = this.expandedDays.has(dateStr);
    return isExpanded;
  }

  // Method to get events for a specific date
  getEventsForDate(dateStr: string): AppointmentEvent[] {
    const events = this.appointments.filter(apt => apt.date === dateStr);

    return events;
  }

  // Method to process events for display (with overflow handling)
  /**
   * Process the component's appointments into an array of EventInput objects ready for calendar display (grouped by date, limited by visible count with an overflow indicator, and annotated with search result classes).
   * @example
   * processEventsForDisplay()
   * [{ title: 'Team Meeting', start: '2026-03-17', classNames: ['search-match'] }]
   * @returns {EventInput[]} Array of EventInput objects prepared for rendering on the calendar.
   **/
  processEventsForDisplay(): EventInput[] {
    const eventsByDate = new Map<string, AppointmentEvent[]>();

    // Group events by date
    this.appointments.forEach(apt => {
      if (!eventsByDate.has(apt.date)) {
        eventsByDate.set(apt.date, []);
      }
      eventsByDate.get(apt.date)!.push(apt);
    });

    const processedEvents: EventInput[] = [];

    eventsByDate.forEach((events, dateStr) => {
      const isExpanded = this.isDayExpanded(dateStr);
      const totalEvents = events.length;

      // Always show all events if day is expanded
      if (isExpanded) {
        events.forEach(apt => {
          const eventInput = this.createEventInput(apt);
          processedEvents.push(eventInput);
        });
      } else if (totalEvents <= this.maxVisibleEvents) {
        // Show all events if 3 or fewer
        events.forEach(apt => {
          processedEvents.push(this.createEventInput(apt));
        });
      } else {
        // Show first 3 events + overflow indicator
        const visibleEvents = events.slice(0, this.maxVisibleEvents);
        const overflowCount = totalEvents - this.maxVisibleEvents;

        // Add visible events
        visibleEvents.forEach(apt => {
          processedEvents.push(this.createEventInput(apt));
        });

        // Add overflow indicator
        processedEvents.push(this.createOverflowEvent(dateStr, overflowCount));
      }
    });

    // Apply search-based opacity classes
    const eventsWithSearchClasses = processedEvents.map(event => {
      if (!this.searchTerm || this.searchTerm.trim() === '') {
        // No search term, all events have full opacity
        return {
          ...event,
          classNames: [...(event.classNames || []), 'search-match'],
        };
      }

      // Apply opacity based on search results
      const isMatch = this.matchesSearchTermForEvent(event, this.searchTerm);
      const classNames = [...(event.classNames || [])];

      if (isMatch) {
        classNames.push('search-match');
      } else {
        classNames.push('search-no-match');
      }

      return {
        ...event,
        classNames,
      };
    });

    return eventsWithSearchClasses;
  }

  // Method to create a regular event input
  /**
   * Create an EventInput object from an AppointmentEvent for use in the calendar.
   * @example
   * createEventInput({ id: '1', aptNumber: 'A1', status: 'confirmed', time: '09:00 - 10:00', date: '2026-01-01' })
   * { id: '1', title: ' A1', className: 'event-confirmed', start: new Date('2026-01-01T09:00:00'), end: new Date('2026-01-01T10:00:00'), extendedProps: { status: 'confirmed', time: '09:00 - 10:00', aptNumber: 'A1', appointmentId: undefined, customerName: undefined, packageName: undefined, employeeName: undefined, appointmentTime: undefined, vehicleMake: undefined, vehicleModel: undefined, vehicleYear: undefined, duration: undefined, isOverflow: false } }
   * @param {{AppointmentEvent}} {{apt}} - AppointmentEvent to convert into an EventInput.
   * @returns {{EventInput}} EventInput object containing id, title, className, start/end or date, and extendedProps.
   **/
  private createEventInput(apt: AppointmentEvent): EventInput {
    const eventData: EventInput = {
      id: apt.id,
      title: ` ${apt.aptNumber}`,
      className: `event-${apt.status}`,
      extendedProps: {
        status: apt.status,
        time: apt.time,
        aptNumber: apt.aptNumber,
        appointmentId: apt.appointmentId,
        customerName: apt.customerName,
        packageName: apt.packageName,
        employeeName: apt.employeeName,
        appointmentTime: apt.appointmentTime,
        vehicleMake: apt.vehicleMake,
        vehicleModel: apt.vehicleModel,
        vehicleYear: apt.vehicleYear,
        duration: apt.duration,
        isOverflow: false,
      },
    };

    // For time-based views, parse the time and create start/end times
    if (apt.time && apt.time !== '+5 more') {
      const timeRange = apt.time.split(' - ');
      if (timeRange.length === 2) {
        const startTime = this.parseTimeToDate(apt.date, timeRange[0]);
        const endTime = this.parseTimeToDate(apt.date, timeRange[1]);
        eventData.start = startTime;
        eventData.end = endTime;
      } else {
        // Fallback to date only for events without proper time format
        eventData.date = apt.date;
      }
    } else {
      // For events without time (like "+5 more"), use date only
      eventData.date = apt.date;
    }

    return eventData;
  }

  // Method to create an overflow event indicator
  /**
   * Create an EventInput representing an overflow indicator for a given date.
   * @example
   * createOverflowEvent('2026-03-17', 3)
   * { id: 'overflow-2026-03-17', title: '+3 more', date: '2026-03-17', className: 'event-pending overflow-indicator', extendedProps: { status: 'pending', isOverflow: true, overflowCount: 3, dateStr: '2026-03-17' } }
   * @param {{string}} dateStr - Date string for the overflow event (e.g., 'YYYY-MM-DD').
   * @param {{number}} overflowCount - Number of additional events indicated by the overflow.
   * @returns {{EventInput}} EventInput representing the overflow indicator event.
   **/
  private createOverflowEvent(dateStr: string, overflowCount: number): EventInput {
    return {
      id: `overflow-${dateStr}`,
      title: `+${overflowCount} more`,
      date: dateStr,
      className: 'event-pending overflow-indicator',
      extendedProps: {
        status: 'pending',
        isOverflow: true,
        overflowCount: overflowCount,
        dateStr: dateStr,
      },
    };
  }

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    firstDay: 0, // Start week on Monday
    views: {
      timeGridWorkWeek: {
        type: 'timeGrid',
        duration: { days: 5 },
        buttonText: 'Work Week',
        firstDay: 1,
        dayHeaderFormat: { day: 'numeric', weekday: 'short' },
      },
    },
    headerToolbar: false, // We'll create custom header
    dayHeaderContent: (args: any) => {
      // Get the current view from the calendar API
      const calendarApi = this.calendarComponent?.getApi();
      const currentView = calendarApi?.view.type;

      // Show date numbers in week views
      if (currentView === 'timeGridWeek' || currentView === 'timeGridWorkWeek') {
        const date = args.date;
        const dayNumber = date.getDate();
        const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
        const dayAbbr = dayNames[date.getDay()];
        return `${dayNumber} - ${dayAbbr}`;
      } else {
        // For month and day views, show only the day abbreviation
        return args.text.substring(0, 3).toUpperCase();
      }
    },
    events: [], // Will be set in ngOnInit
    datesSet: this.handleDatesSet.bind(this),
    eventClick: this.handleEventClick.bind(this),
    dateClick: this.handleDateClick.bind(this),
    eventMouseEnter: this.handleEventMouseEnter.bind(this),
    eventMouseLeave: this.handleEventMouseLeave.bind(this),
    dayCellContent: (args: any) => {
      return { html: args.dayNumberText };
    },
    dayCellDidMount: (args: any) => {
      this.handleDayCellMount(args);
    },
    height: 'auto',
    aspectRatio: 1.35,
    dayMaxEvents: false, // We'll handle overflow manually
    eventDisplay: 'block',
    displayEventTime: false,
    eventOrder: (a: any, b: any) => {
      const statusOrder: Record<string, number> = {
        confirmed: 1,
        rescheduled: 2,
        cancelled: 3,
        pending: 4,
      };
      return statusOrder[a.extendedProps?.status] - statusOrder[b.extendedProps?.status];
    },
    // Time grid configuration
    slotMinTime: '01:00:00',
    slotMaxTime: '24:59:00',
    slotDuration: '01:00:00',
    slotLabelInterval: '01:00:00',
    slotLabelFormat: {
      hour: 'numeric',
      meridiem: 'short',
    },
    nowIndicator: true,
    scrollTime: '08:00:00',
    allDaySlot: false,
    // Custom event rendering
    eventContent: (arg: any) => {
      return this.renderEventContent(arg);
    },
    // Drag and drop configuration
    editable: true,
    eventDrop: this.handleEventDrop.bind(this),
    eventDragStart: this.handleEventDragStart.bind(this),
    eventDragStop: this.handleEventDragStop.bind(this),
  };

  /**
   * Initialize the appointments calendar component: load locations and packages, set up location subscription, load appointments, update the calendar date, and initialize the date picker.
   * @example
   * gOnInit()
   * undefined
   * @returns {void} No return value.
   **/
  ngOnInit(): void {
    // Load locations and packages for filter mapping
    this.profileService.loadLocations();
    this.packageService.loadPackages();

    // Set up location subscription to ensure default location is used
    this.setupLocationSubscription();

    this.loadAppointments();
    this.updateCalendarDate();
    this.initializeDatePicker();
  }

  /**
   * Subscribes to profileService locations, sets a default location if none is selected, and reloads appointments.
   * @example
   * setupLocationSubscription()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} Initializes a subscription to locations and may trigger loading of appointments.
   **/
  private setupLocationSubscription(): void {
    // Subscribe to locations and set default location if none is selected
    this.locationsSubscription = this.profileService.locations$.subscribe(locations => {
      if (
        locations.length > 0 &&
        (!this.selectedLocationLabels || this.selectedLocationLabels.length === 0)
      ) {
        // Set the first location as default if no location is currently selected
        this.selectedLocationLabels = [locations[0].branch];

        // Reload appointments with the default location
        this.loadAppointments();
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Responds to Angular input property changes to apply a search filter or reload appointments when relevant inputs change.
   * @example
   * gOnChanges({ searchTerm: { firstChange: false, currentValue: 'foo' } })
   * void
   * @param {{SimpleChanges}} {{changes}} - Object containing changed input properties mapped to SimpleChange objects.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchTerm'] && !changes['searchTerm'].firstChange) {
      this.applySearchFilter();
    }

    if (changes['selectedLocation'] && !changes['selectedLocation'].firstChange) {
      this.loadAppointments();
    }

    if (changes['selectedLocationLabels'] && !changes['selectedLocationLabels'].firstChange) {
      this.loadAppointments();
    }

    if (changes['activeFilters'] && !changes['activeFilters'].firstChange) {
      this.loadAppointments();
    }
  }

  /**
   * Prepare API query parameters (default date range, optional search and location filters), set loading state, and initiate fetching appointments.
   * @example
   * oadAppointments()
   * void
   * @returns {void} Does not return a value; triggers appointment loading and API calls as side effects.
   */
  loadAppointments(): void {
    this.isLoading = true;

    // Build API parameters
    const params: any = {};

    // Add default date range (6 months before to 3 months after today)
    const defaultDateRange = this.getDefaultDateRange();
    params.start_date = defaultDateRange.startDate;
    params.end_date = defaultDateRange.endDate;

    // Add search term if provided
    if (this.searchTerm && this.searchTerm.trim()) {
      params.search = this.searchTerm.trim();
    }

    // Add location filter if selected
    if (this.selectedLocationLabels && this.selectedLocationLabels.length > 0) {
      params.branch = this.selectedLocationLabels.join(',');
    } else {
      // If no location is selected, try to get default location from service
      const currentLocations = (this.profileService.locations$ as any).source?.value || [];
      if (currentLocations.length > 0) {
        params.branch = currentLocations[0].branch;
      }
    }

    // Add filters if active
    if (this.activeFilters) {
      // Map filter arrays to API parameters
      this.mapFiltersToApiParams(params);
    } else {
      // No modal filters, call API directly
      this.callAppointmentAPI(params);
    }
  }

  /**
   * Return the default date range spanning from the first day of the previous month to the last day of the next month, computed from this.currentDate.
   * @example
   * getDefaultDateRange()
   * { startDate: "2024-02-01", endDate: "2024-04-30" }
   * @returns {{ startDate: string; endDate: string }} Object containing formatted startDate and endDate as local-key strings.
   **/
  private getDefaultDateRange(): { startDate: string; endDate: string } {
    const { start, end } = this.getRangeToFetch();
    return {
      startDate: this.formatDateAsLocalKey(start),
      endDate: this.formatDateAsLocalKey(end),
    };
  }

  /**
   * Window to request from the API: the rendered range plus padding, so stepping a day or a
   * week at a time does not refetch every time. Falls back to the month around `currentDate`
   * before FullCalendar has reported a range (first load).
   * @returns {{ start: Date; end: Date }} Inclusive date range to fetch.
   **/
  private getRangeToFetch(): { start: Date; end: Date } {
    if (this.visibleRange) {
      const start = new Date(this.visibleRange.start);
      start.setDate(start.getDate() - this.fetchRangePaddingDays);
      const end = new Date(this.visibleRange.end);
      end.setDate(end.getDate() + this.fetchRangePaddingDays);
      return { start, end };
    }

    const currentMonth = this.currentDate.getMonth();
    const currentYear = this.currentDate.getFullYear();
    return {
      start: new Date(currentYear, currentMonth - 1, 1),
      // Day 0 of month+2 is the last day of month+1.
      end: new Date(currentYear, currentMonth + 2, 0),
    };
  }

  /**
   * FullCalendar reports the rendered range on first paint and on every navigation or view
   * change. Reload whenever that range is not inside the window already fetched (CWS-587).
   * @param {{ start: Date; end: Date }} info - Rendered range; `end` is exclusive.
   * @returns {void} No return value; may trigger an appointments reload.
   **/
  private handleDatesSet(info: { start: Date; end: Date }): void {
    const visibleStart = new Date(info.start);
    const visibleEnd = new Date(info.end);
    // `end` is exclusive — step back a day so comparisons are inclusive on both ends.
    visibleEnd.setDate(visibleEnd.getDate() - 1);
    this.visibleRange = { start: visibleStart, end: visibleEnd };

    if (this.isRangeAlreadyFetched(visibleStart, visibleEnd)) {
      return;
    }
    this.loadAppointments();
  }

  /**
   * True when the given range lies inside the window the last request asked for.
   * Local date keys are `YYYY-MM-DD`, so lexicographic comparison is chronological.
   * @param {Date} start - Inclusive range start.
   * @param {Date} end - Inclusive range end.
   * @returns {boolean} Whether the range is already covered by fetched data.
   **/
  private isRangeAlreadyFetched(start: Date, end: Date): boolean {
    if (!this.fetchedRange) {
      return false;
    }
    return (
      this.formatDateAsLocalKey(start) >= this.fetchedRange.start &&
      this.formatDateAsLocalKey(end) <= this.fetchedRange.end
    );
  }

  /**
   * Shift a date by one navigation step without mutating the original. Month steps are built
   * from the 1st because `setMonth` on a 29-31 day date overflows (31 Aug + 1 month = 1 Oct),
   * which made the fetched window drift away from the month on screen (CWS-587).
   * @param {Date} date - Date to shift.
   * @param {'day' | 'week' | 'month'} view - Current calendar view.
   * @param {number} step - -1 for previous, 1 for next.
   * @returns {Date} A new shifted Date.
   **/
  private shiftDate(date: Date, view: 'day' | 'week' | 'month', step: number): Date {
    const shifted = new Date(date);
    if (view === 'month') {
      return new Date(date.getFullYear(), date.getMonth() + step, 1);
    }
    shifted.setDate(shifted.getDate() + (view === 'week' ? 7 * step : step));
    return shifted;
  }

  /**
   * Map activeFilters into API query parameters and call the appointment API (may perform async employee name mapping).
   * @example
   * mapFiltersToApiParams({ page: 1, per_page: 20 })
   * void
   * @param {{any}} {{params}} - The mutable params object that will be populated with mapped API query fields.
   * @returns {{void}} Does not return a value; triggers the appointment API call (may be deferred while mapping employee names).
   **/
  private mapFiltersToApiParams(params: any): void {
    if (!this.activeFilters) return;

    // Get current data from observables to map IDs to names
    let currentLocations: any[] = [];
    let currentPackages: any[] = [];

    try {
      currentLocations = (this.profileService.locations$ as any).source?.value || [];
      currentPackages = (this.packageService.packages$ as any).source?.value || [];
    } catch (error) {
      console.warn('⚠️ Could not access service data for filter mapping:', error);
    }

    // Map appointment statuses
    if (this.activeFilters.statuses.length > 0) {
      params.appointment_status = this.activeFilters.statuses.join(',');
    }

    // Map employee IDs to names
    if (this.activeFilters.employees.length > 0) {
      this.mapEmployeeIdsToNames(this.activeFilters.employees)
        .then(employeeNames => {
          params.employee_name = employeeNames.join(',');

          // Re-call the API with the updated params including employee names
          this.callAppointmentAPI(params);
        })
        .catch(error => {
          console.error('❌ Error mapping employee IDs to names:', error);
          // Continue with the API call without employee filter
          this.callAppointmentAPI(params);
        });

      // Return early to wait for employee name mapping
      return;
    }

    // Map location IDs to branch names (from modal filters)
    if (this.activeFilters.locations.length > 0) {
      const selectedLocationNames = currentLocations
        .filter((loc: any) => this.activeFilters!.locations.includes(loc.id))
        .map((loc: any) => loc.branch);
      if (selectedLocationNames.length > 0) {
        // Modal location filters override dropdown location filter
        params.branch = selectedLocationNames.join(',');
      }
    }

    // Map package IDs to package names
    if (this.activeFilters.services.length > 0) {
      const selectedPackageNames = currentPackages
        .filter((pkg: any) => this.activeFilters!.services.includes(pkg.id))
        .map((pkg: any) => pkg.package_name);
      if (selectedPackageNames.length > 0) {
        params.package_name = selectedPackageNames.join(',');
      }
    }

    // If no employees to map, call API directly
    this.callAppointmentAPI(params);
  }

  private async mapEmployeeIdsToNames(employeeIds: string[]): Promise<string[]> {
    return mapEmployeeIdsToNames(employeeIds, this.profileService, this.apiService);
  }

  /**
   * Fetch appointments from the API, ensuring default date range is included, then update component state and calendar events.
   * @example
   * callAppointmentAPI({ start_date: '2026-03-01', end_date: '2026-03-31' })
   * undefined
   * @param {{any}} {{params}} - Query parameters for fetching appointments; if start_date or end_date are missing they will be set to defaults.
   * @returns {{void}} Does not return a value; performs an asynchronous request and updates component properties and calendar state.
   **/
  private callAppointmentAPI(params: any): void {
    // Ensure default date parameters are always included
    if (!params.start_date || !params.end_date) {
      const defaultDateRange = this.getDefaultDateRange();
      params.start_date = params.start_date || defaultDateRange.startDate;
      params.end_date = params.end_date || defaultDateRange.endDate;
    }

    // Date-only bounds are resolved server-side against a timezone; without this the API
    // falls back to America/Los_Angeles and the window edges shift for non-Pacific shops.
    const timezone = getUserTimezone();
    if (timezone && !params.timezone) {
      params.timezone = timezone;
    }

    // Claim the window before the request resolves: the first `datesSet` fires while the
    // initial load is still in flight, and without this it would fire a duplicate request.
    this.fetchedRange = { start: params.start_date, end: params.end_date };

    this.appointmentService.getAppointments(params).subscribe({
      next: response => {
        if (response.data && response.data.appointments) {
          this.originalAppointments = response.data.appointments; // Store original data
          this.appointments = this.transformAppointments(response.data.appointments);

          if (this.calendarComponent && this.calendarComponent.getApi()) {
            this.refreshCalendarEvents();
          } else {
            // Calendar not initialized yet, set events for initial load
            this.calendarOptions.events = this.getFormattedEvents();
          }
        }

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: error => {
        console.error('❌ Calendar - Error fetching appointments:', error);
        // Leave the window unfetched so the next navigation retries instead of showing a
        // silently empty calendar.
        this.fetchedRange = null;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Load dummy appointments, filter by selected location, transform them and update the calendar events.
   * @example
   * loadDummyAppointments()
   * Sets component appointments and calendar events (void)
   * @param {void} none - No arguments.
   * @returns {void} Does not return a value; updates component state and calendar events.
   */
  private loadDummyAppointments(): void {
    // Dummy appointment data matching the Appointment model structure
    const allDummyAppointments: Appointment[] = [
      {
        id: '1',
        appointment_time: '2025-08-19T10:00:00.000Z',
        appointment_code: 'APT001',
        appointment_status: 'confirmed',
        branch: 'Downtown Branch',
        created_at: '2025-08-15T08:00:00.000Z',
        customer_id: 'cust_001',
        customer_name: 'John Smith',
        duration: { minutes: 60 },
        employee_id: 'emp_001',
        employee_name: 'Mike Johnson',
        location_id: 'loc_001',
        package_id: 'pkg_001',
        package_name: 'Premium Wash',
        source: 'online',
        updated_at: '2025-08-15T08:00:00.000Z',
        vehicle_make: 'Toyota',
        vehicle_model: 'Camry DLX',
        vehicle_year: 1985,
        price: '59.00',
      },
      {
        id: '2',
        appointment_time: '2025-08-20T14:30:00.000Z',
        appointment_code: 'APT002',
        appointment_status: 'pending',
        branch: 'Downtown Branch',
        created_at: '2025-08-15T09:00:00.000Z',
        customer_id: 'cust_002',
        customer_name: 'Sarah Davis',
        duration: { minutes: 45 },
        employee_id: 'emp_002',
        employee_name: 'Alex Brown',
        location_id: 'loc_001',
        package_id: 'pkg_002',
        package_name: 'Basic Wash',
        source: 'phone',
        updated_at: '2025-08-15T09:00:00.000Z',
        vehicle_make: 'Honda',
        vehicle_model: 'Civic',
        vehicle_year: 2022,
        price: '39.00',
      },
      {
        id: '3',
        appointment_time: '2025-08-21T09:00:00.000Z',
        appointment_code: 'APT003',
        appointment_status: 'confirmed',
        branch: 'North Branch',
        created_at: '2025-08-16T10:00:00.000Z',
        customer_id: 'cust_003',
        customer_name: 'Robert Wilson',
        duration: { minutes: 90 },
        employee_id: 'emp_003',
        employee_name: 'Lisa Garcia',
        location_id: 'loc_002',
        package_id: 'pkg_003',
        package_name: 'Deluxe Detailing',
        source: 'online',
        updated_at: '2025-08-16T10:00:00.000Z',
        vehicle_make: 'BMW',
        vehicle_model: 'X5',
        vehicle_year: 2023,
        price: '49.00',
      },
      {
        id: '4',
        appointment_time: '2025-08-21T15:00:00.000Z',
        appointment_code: 'APT004',
        appointment_status: 'cancelled',
        branch: 'South Branch',
        created_at: '2025-08-17T11:00:00.000Z',
        customer_id: 'cust_004',
        customer_name: 'Emily Taylor',
        duration: { minutes: 30 },
        employee_id: 'emp_004',
        employee_name: 'David Martinez',
        location_id: 'loc_003',
        package_id: 'pkg_004',
        package_name: 'Quick Rinse',
        source: 'walk-in',
        updated_at: '2025-08-17T11:00:00.000Z',
        vehicle_make: 'Mercedes',
        vehicle_model: 'C-Class',
        vehicle_year: 2021,
        price: '29.00',
      },
      {
        id: '5',
        appointment_time: '2025-10-24T14:00:00.000Z',
        appointment_code: 'APT0000061',
        appointment_status: 'confirmed',
        branch: 'Downtown Branch',
        created_at: '2025-10-20T08:00:00.000Z',
        customer_id: 'cust_005',
        customer_name: 'Sneha Scaria',
        duration: { hours: 5, minutes: 0 },
        employee_id: 'emp_005',
        employee_name: 'Thomas Renu',
        location_id: 'loc_001',
        package_id: 'pkg_005',
        package_name: 'Detailing Services',
        source: 'online',
        updated_at: '2025-10-20T08:00:00.000Z',
        vehicle_make: 'CHEVROLET',
        vehicle_model: 'TRAILBLAZER',
        vehicle_year: 2023,
        price: '150.00',
      },
    ];

    // Filter appointments based on selected location
    let dummyAppointments: Appointment[];
    if (this.selectedLocationLabels && this.selectedLocationLabels.length > 0) {
      dummyAppointments = allDummyAppointments.filter(apt =>
        this.selectedLocationLabels.includes(apt.branch)
      );
    } else {
      // If no location is selected, use default location (first location)
      const currentLocations = (this.profileService.locations$ as any).source?.value || [];
      if (currentLocations.length > 0) {
        const defaultLocation = currentLocations[0].branch;
        dummyAppointments = allDummyAppointments.filter(apt => apt.branch === defaultLocation);
      } else {
        dummyAppointments = allDummyAppointments;
      }
    }

    this.originalAppointments = dummyAppointments;
    this.appointments = this.transformAppointments(dummyAppointments);

    if (this.calendarComponent && this.calendarComponent.getApi()) {
      this.refreshCalendarEvents();
    } else {
      // Calendar not initialized yet, set events for initial load
      this.calendarOptions.events = this.getFormattedEvents();
    }

    this.isLoading = false;
  }

  /**
   * Convert an array of Appointment objects into AppointmentEvent objects suitable for calendar display.
   * @example
   * transformAppointments([{ id: 1, appointment_code: 'APT123', appointment_time: '2026-03-17T10:00:00Z', duration: { hours: 1, minutes: 0 }, appointment_status: 'confirmed', customer_name: 'John Doe', package_name: 'Full Service', employee_name: 'Jane Smith', vehicle_make: 'Toyota', vehicle_model: 'Camry', vehicle_year: 2018 }])
   * [{ id: 'APT123', title: ' APT123', date: '2026-03-17', status: 'confirmed', time: '10:00 AM - 11:00 AM', aptNumber: 'APT123', appointmentId: 1, customerName: 'John Doe', packageName: 'Full Service', employeeName: 'Jane Smith', appointmentTime: '2026-03-17T10:00:00Z', vehicleMake: 'Toyota', vehicleModel: 'Camry', vehicleYear: '2018', duration: { hours: 1, minutes: 0 } }]
   * @param {{Appointment[]}} {{appointments}} - Array of raw Appointment objects to transform.
   * @returns {{AppointmentEvent[]}} Array of transformed AppointmentEvent objects for calendar use.
   **/
  private transformAppointments(appointments: Appointment[]): AppointmentEvent[] {
    if (!appointments) {
      return [];
    }
    return appointments.map(apt => {
      const startTime = new Date(apt.appointment_time);
      // Calculate duration in minutes (considering both hours and minutes)
      const durationMinutes = (apt.duration?.hours || 0) * 60 + (apt.duration?.minutes || 60);
      const endTime = new Date(startTime.getTime() + durationMinutes * 60000);

      const formatTime = (date: Date) => {
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
      };

      let calendarStatus: 'completed' | 'confirmed' | 'rescheduled' | 'cancelled' | 'pending' =
        'pending';
      switch (apt.appointment_status) {
        case 'confirmed':
          calendarStatus = 'confirmed';
          break;
        case 'completed':
          calendarStatus = 'completed';
          break;
        case 'cancelled':
          calendarStatus = 'cancelled';
          break;
        case 'pending':
          calendarStatus = 'pending';
          break;
      }

      return {
        // FullCalendar keys events by id; appointment_code can repeat or be blank, so use
        // the appointment id and keep the code for display.
        id: `${apt.id ?? apt.appointment_code}`,
        title: ` ${apt.appointment_code}`,
        date: this.formatDateAsLocalKey(startTime),
        status: calendarStatus,
        time: `${formatTime(startTime)} - ${formatTime(endTime)}`,
        aptNumber: apt.appointment_code, // Just the code without "APT" prefix
        appointmentId: apt.id, // Store the appointment ID for fetching details
        customerName: apt.customer_name,
        packageName: apt.package_name,
        employeeName: apt.employee_name,
        appointmentTime: apt.appointment_time,
        // Vehicle data from API
        vehicleMake: apt.vehicle_make,
        vehicleModel: apt.vehicle_model,
        vehicleYear: apt.vehicle_year?.toString(),
        // Duration data
        duration: apt.duration,

        // id: `${apt.appointment_code}`,
        // appointment_time: apt.appointment_time,
        // appointment_code: apt.appointment_code,
        // appointment_status: apt.appointment_status,
        // branch: apt.branch,
        // created_at: apt.created_at,
        // customer_id: apt.customer_id,
        // customer_name: apt.customer_name,
        // duration: apt.duration,
        // employee_id: apt.employee_id,
        // employee_name: apt.employee_name,
        // location_id: apt.location_id,
        // package_id: apt.package_id,
        // package_name: apt.package_name,
        // source: apt.source,
        // updated_at: apt.updated_at,
        // vehicle_make: apt.vehicle_make,
        // vehicle_model: apt.vehicle_model,
        // vehicle_year: apt.vehicle_year
      };
    });
  }

  private refreshCalendarEvents(): void {
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.removeAllEvents();
    calendarApi.addEventSource(this.processEventsForDisplay());

    // Update day cell classes after a brief delay to ensure DOM is updated
    setTimeout(() => {
      this.updateDayCellClasses();
    }, 100);
  }

  // Method to update day cell classes for expanded days
  /**
   * Update CSS classes on calendar day cells to reflect whether their event containers are expanded or collapsed.
   * @example
   * updateDayCellClasses()
   * undefined
   * @returns {{void}} Does not return a value.
   **/
  private updateDayCellClasses(): void {
    const dayCells = document.querySelectorAll('.fc-daygrid-day');
    dayCells.forEach((dayCell: Element) => {
      // Get the actual date from the cell's data attribute
      const dateAttr = dayCell.getAttribute('data-date');
      if (dateAttr) {
        const dateStr = dateAttr;
        const eventsContainer = dayCell.querySelector('.fc-daygrid-day-events');

        if (eventsContainer) {
          if (this.isDayExpanded(dateStr)) {
            eventsContainer.classList.add('expanded');
          } else {
            eventsContainer.classList.remove('expanded');
          }
        }
      }
    });
  }

  getFormattedEvents(): EventInput[] {
    return this.processEventsForDisplay();
  }

  /**
   * Convert a "YYYY-MM-DD" date string and a 12-hour time string (e.g., "10:00 AM") into a local Date object.
   * @example
   * parseTimeToDate('2026-03-17', '10:30 PM')
   * // Wed Mar 17 2026 22:30:00 GMT+... (local timezone)
   * @param {string} dateStr - Date string in "YYYY-MM-DD" format.
   * @param {string} timeStr - Time string in 12-hour format, e.g. "10:00 AM" or "2:30 PM".
   * @returns {Date} A Date object representing the combined local date and time.
   **/
  private parseTimeToDate(dateStr: string, timeStr: string): Date {
    // Parse YYYY-MM-DD as a local date to avoid UTC day shifts.
    // Using `new Date('YYYY-MM-DD')` is interpreted as UTC in JS.
    const [yearStr, monthStr, dayStr] = dateStr.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    const date = new Date(year, month - 1, day);
    const time = timeStr.trim();

    // Parse time format like "10:00 AM" or "2:30 PM"
    const timeMatch = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const meridiem = timeMatch[3].toUpperCase();

      if (meridiem === 'PM' && hours !== 12) {
        hours += 12;
      } else if (meridiem === 'AM' && hours === 12) {
        hours = 0;
      }

      date.setHours(hours, minutes, 0, 0);
    }

    return date;
  }

  /**
   * Checks whether the duration represented by a time range string is greater than one hour.
   * @example
   * this.isDurationGreaterThanOneHour("09:00 - 10:30")
   * true
   * @param {string} timeRange - A time range string in the format "start - end" (e.g., "09:00 - 10:30").
   * @returns {boolean} True if the duration between start and end is greater than one hour, otherwise false.
   */
  private isDurationGreaterThanOneHour(timeRange: string): boolean {
    const timeParts = timeRange.split(' - ');
    if (timeParts.length !== 2) {
      return false;
    }

    const startTime = timeParts[0].trim();
    const endTime = timeParts[1].trim();

    // Parse start and end times
    const startDate = this.parseTimeToDate('2024-01-01', startTime);
    const endDate = this.parseTimeToDate('2024-01-01', endTime);

    // Calculate duration in milliseconds
    const durationMs = endDate.getTime() - startDate.getTime();

    // Convert to hours (1 hour = 60 * 60 * 1000 milliseconds)
    const durationHours = durationMs / (60 * 60 * 1000);

    // Return true if duration is greater than 1 hour
    return durationHours > 1;
  }

  /**
   * Update the calendar to the component's currentDate and, if the current view is 'month', reload appointments.
   * @example
   * pdateCalendarDate()
   * // returns void
   * @returns {void} Does not return a value.
   */
  updateCalendarDate(): void {
    if (this.calendarComponent) {
      const calendarApi = this.calendarComponent.getApi();
      calendarApi.gotoDate(this.currentDate);
      // `datesSet` reloads when the rendered range moves outside the fetched window.
    }
  }

  /**
   * Compute the header text for the appointments calendar based on the current view (day, week, or month).
   * @example
   * // Day view
   * component.currentView = 'day';
   * component.currentDate = new Date('2023-03-15');
   * component.currentHeaderText
   * "Wednesday, Mar 15 2023"
   * // Week view (work week)
   * component.currentView = 'week';
   * component.weekType = 'work';
   * component.currentDate = new Date('2023-03-15');
   * component.currentHeaderText
   * "Mar 13 - Mar 17, 2023"
   * // Month view
   * component.currentView = 'month';
   * component.currentDate = new Date('2023-03-15');
   * component.currentHeaderText
   * "March 2023"
   * @returns {string} Formatted header string for the current calendar view.
   */
  get currentHeaderText(): string {
    switch (this.currentView) {
      case 'day': {
        const weekday = this.currentDate.toLocaleDateString('en-US', { weekday: 'long' });
        const dayMonth = this.currentDate.toLocaleDateString('en-US', { month: 'short' });
        const dayNum = this.currentDate.getDate();
        const dayYear = this.currentDate.getFullYear();
        return `${weekday}, ${dayMonth} ${dayNum} ${dayYear}`;
      }
      case 'week': {
        const startOfWeek =
          this.weekType === 'work'
            ? this.getStartOfWorkWeek(this.currentDate)
            : this.getStartOfWeek(this.currentDate);
        const endOfWeek = new Date(startOfWeek);

        // Adjust end date based on week type
        if (this.weekType === 'work') {
          endOfWeek.setDate(startOfWeek.getDate() + 4); // Monday to Friday (5 days)
        } else {
          endOfWeek.setDate(startOfWeek.getDate() + 6); // Full week (7 days)
        }

        const startMonth = startOfWeek.toLocaleDateString('en-US', { month: 'short' });
        const startDay = startOfWeek.getDate();
        const endMonth = endOfWeek.toLocaleDateString('en-US', { month: 'short' });
        const endDay = endOfWeek.getDate();
        const year = startOfWeek.getFullYear();

        if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
          return `${startMonth} ${startDay} - ${endDay}, ${year}`;
        } else {
          return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
        }
      }
      case 'month':
      default:
        return this.currentDate.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        });
    }
  }

  private getStartOfWeek(date: Date): Date {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day; // Sunday is 0
    startOfWeek.setDate(diff);
    return startOfWeek;
  }

  private getStartOfWorkWeek(date: Date): Date {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    // For work week, we want Monday (1) to be the start
    // Sunday is 0, Monday is 1, Tuesday is 2, etc.
    // To get to Monday, we need to go back (day - 1) days
    // But if it's Sunday (day = 0), we need to go back 6 days
    const diff = day === 0 ? -6 : -(day - 1);
    startOfWeek.setDate(startOfWeek.getDate() + diff);
    return startOfWeek;
  }

  private getNextWorkWeekStart(date: Date): Date {
    const currentWorkWeekStart = this.getStartOfWorkWeek(date);
    const nextWorkWeekStart = new Date(currentWorkWeekStart);
    nextWorkWeekStart.setDate(nextWorkWeekStart.getDate() + 7); // Move to next Monday
    return nextWorkWeekStart;
  }

  private getPreviousWorkWeekStart(date: Date): Date {
    const currentWorkWeekStart = this.getStartOfWorkWeek(date);
    const prevWorkWeekStart = new Date(currentWorkWeekStart);
    prevWorkWeekStart.setDate(prevWorkWeekStart.getDate() - 7); // Move to previous Monday
    return prevWorkWeekStart;
  }

  /**
   * Navigate the calendar to the previous or next period and update internal state.
   * @example
   * navigateMonth('prev')
   * undefined
   * @param {{'prev' | 'next'}} {{direction}} - Direction to navigate; 'prev' moves backward, 'next' moves forward.
   * @returns {{void}} No return value.
   **/
  navigateMonth(direction: 'prev' | 'next'): void {
    if (this.calendarComponent) {
      const calendarApi = this.calendarComponent.getApi();
      if (direction === 'prev') {
        calendarApi.prev();
      } else {
        calendarApi.next();
      }

      // Keep our internal currentDate in step with the calendar API (see shiftDate for why
      // month steps are not done with setMonth).
      this.currentDate = this.shiftDate(
        this.currentDate,
        this.currentView,
        direction === 'prev' ? -1 : 1
      );

      // Clear expanded days when navigating to a new month
      if (this.currentView === 'month') {
        this.expandedDays.clear();
      }

      // Reset scroll position to top when navigating to new month

      // No reload here: `datesSet` fires for every navigation — month, week and day — and
      // reloads when the newly rendered range falls outside the fetched window.
    }
  }

  /**
   * Switches the calendar to the specified view ('day', 'week', or 'month') and updates component state and the calendar API.
   * @example
   * hangeView('week')
   * undefined
   * @param {{('day'|'week'|'month')}} {{view}} - Target view to switch to; updates currentView/currentDate, navigates the calendar, and triggers loading or refreshing of events.
   * @returns {{void}} No return value; performs side effects on the component and calendar API.
   **/
  changeView(view: 'day' | 'week' | 'month'): void {
    this.currentView = view;
    if (this.calendarComponent) {
      const calendarApi = this.calendarComponent.getApi();

      // Always navigate to current date when switching views
      const today = new Date();

      switch (view) {
        case 'day':
          // Navigate to today's date for day view
          this.currentDate = new Date(today);
          calendarApi.gotoDate(today);
          calendarApi.changeView('timeGridDay');
          break;
        case 'week': {
          // Use the appropriate week view based on weekType
          const weekView = this.weekType === 'work' ? 'timeGridWorkWeek' : 'timeGridWeek';

          // Navigate to the current week (containing today)
          let targetDate: Date;
          if (this.weekType === 'work') {
            targetDate = this.getStartOfWorkWeek(today);
          } else {
            targetDate = this.getStartOfWeek(today);
          }

          this.currentDate = new Date(today);
          calendarApi.gotoDate(targetDate);
          calendarApi.changeView(weekView);
          break;
        }
        case 'month':
          // Navigate to current month for month view
          this.currentDate = new Date(today);
          calendarApi.gotoDate(today);
          calendarApi.changeView('dayGridMonth');
          break;
      }

      // Clear expanded days when switching views (since overflow only applies to month view)
      if (view !== 'month') {
        this.expandedDays.clear();
      }

      // Reload appointments with new date range when switching to month view
      if (view === 'month') {
        this.loadAppointments();
      } else {
        // Refresh events to ensure proper rendering with new view
        setTimeout(() => {
          this.refreshCalendarEvents();
        }, 100);
      }
    }
  }

  /**
   * Switches the calendar between full week and work week views, navigates to the start of the current week, updates the current date, and refreshes events.
   * @example
   * hangeWeekType('work')
   * undefined
   * @param {{'full'|'work'}} {{weekType}} - 'full' to show Sunday-Saturday week view or 'work' to show Monday-Friday work week; when the current view is 'week' and calendar is available, it navigates to the appropriate start-of-week and updates the view.
   * @returns {{void}} No return value.
   **/
  changeWeekType(weekType: 'full' | 'work'): void {
    this.weekType = weekType;
    if (this.currentView === 'week' && this.calendarComponent) {
      const calendarApi = this.calendarComponent.getApi();
      const weekView = weekType === 'work' ? 'timeGridWorkWeek' : 'timeGridWeek';

      // Always navigate to the current week (containing today) when switching week types
      const today = new Date();
      let targetDate: Date;

      if (weekType === 'work') {
        // For work week, navigate to the Monday of the current week
        targetDate = this.getStartOfWorkWeek(today);
      } else {
        // For full week, navigate to the Sunday of the current week
        targetDate = this.getStartOfWeek(today);
      }

      // Update currentDate to today
      this.currentDate = new Date(today);

      calendarApi.gotoDate(targetDate);
      calendarApi.changeView(weekView);

      // Refresh events to ensure proper rendering with new view
      setTimeout(() => {
        this.refreshCalendarEvents();
      }, 100);
    }
  }

  /**
   * Handle a calendar event click; toggles day expansion when the clicked event is an overflow marker.
   * @example
   * andleEventClick({ event: { extendedProps: { isOverflow: true, dateStr: '2026-03-17' } } })
   * undefined
   * @param {{any}} {{clickInfo}} - Click info object supplied by the calendar, expected to include event.extendedProps (isOverflow and dateStr).
   * @returns {{void}} No return value.
   **/
  handleEventClick(clickInfo: any): void {
    const eventProps = clickInfo.event.extendedProps;

    // Check if this is an overflow event
    if (eventProps.isOverflow) {
      // Toggle expansion for this day
      this.toggleDayExpansion(eventProps.dateStr);
      return;
    }
  }

  /**
   * Handle a click on a calendar day cell and update the selected CSS class on day elements.
   * @example
   * andleDateClick({ dayEl: document.querySelector('.fc-daygrid-day') })
   * undefined
   * @param {{any}} {{dateInfo}} - Object containing information about the clicked date (may include dayEl DOM element).
   * @returns {{void}} No return value.
   **/
  handleDateClick(dateInfo: any): void {
    // Remove selected class from all day cells
    const allDayCells = document.querySelectorAll('.fc-daygrid-day');
    allDayCells.forEach(cell => cell.classList.remove('selected'));

    // Add selected class to the clicked day cell

    if (dateInfo.dayEl) {
      dateInfo.dayEl.classList.add('selected');
    }
  }

  handleEventMouseEnter(mouseEnterInfo: any): void {
    const event = mouseEnterInfo.event;
    const eventProps = event.extendedProps;

    // Don't show tooltip for overflow events
    if (eventProps.isOverflow) {
      return;
    }

    this.showEventTooltip(mouseEnterInfo.el, eventProps);
  }

  /**
   * Handle mouseleave on a calendar event: conditionally delay hiding the event tooltip.
   * @example
   * andleEventMouseLeave(mouseLeaveInfo)
   * undefined
   * @param {{any}} {{mouseLeaveInfo}} - Mouse leave info object from the calendar callback (contains event and jsEvent).
   * @returns {{void}} No return value; may schedule a tooltip hide after a 350ms delay unless the event is overflow or the cursor moved into the tooltip.
   **/
  handleEventMouseLeave(mouseLeaveInfo: any): void {
    const event = mouseLeaveInfo.event;
    const eventProps = event.extendedProps;

    // Don't hide tooltip for overflow events (since we don't show them)
    if (eventProps.isOverflow) {
      return;
    }

    // Keep tooltip open when cursor moves from event into tooltip content.
    const relatedTarget = mouseLeaveInfo?.jsEvent?.relatedTarget as EventTarget | null;
    if (this.isTooltipTarget(relatedTarget)) {
      return;
    }

    // Delay hiding to allow mouse movement to tooltip
    this.tooltipHideTimeout = setTimeout(() => {
      this.hideEventTooltip();
    }, 350);
  }

  /**
   * Show a detailed appointment tooltip anchored to an element and populated from event properties.
   * @example
   * showEventTooltip(document.querySelector('#slot'), { appointmentTime: '2026-01-01T10:00:00Z', duration: 60, status: 'scheduled', appointmentId: 'apt123' })
   * undefined
   * @param {{HTMLElement}} {{element}} - The DOM element to which the tooltip will be anchored and positioned.
   * @param {{any}} {{eventProps}} - Appointment/event properties used to populate the tooltip (appointmentTime, duration, status, customerName, vehicle info, employeeName, etc.).
   * @returns {{void}} Returns nothing; the tooltip DOM is appended to document.body and its lifecycle is managed internally.
   **/
  private showEventTooltip(element: HTMLElement, eventProps: any): void {
    // Clear any pending hide timeout
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }

    // Remove existing tooltip
    this.hideEventTooltip();

    // Create tooltip container
    const tooltipContainer = document.createElement('div');
    tooltipContainer.className = 'appointment-tooltip-container';
    // Initialize with offscreen position to prevent flash
    tooltipContainer.style.position = 'fixed';
    tooltipContainer.style.left = '-9999px';
    tooltipContainer.style.top = '-9999px';

    // Create tooltip arrow
    const arrow = document.createElement('div');
    arrow.className = 'tooltip-arrow';

    // Create tooltip content
    const tooltip = document.createElement('div');
    tooltip.className = `appointment-tooltip tooltip-status-${eventProps.status}`;

    // Format appointment date and time
    const appointmentDate = new Date(eventProps.appointmentTime);
    const formattedDate = appointmentDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    // Calculate correct time range from duration using the same method as event chips
    const timeString = this.calculateTimeRange(eventProps.appointmentTime, eventProps.duration);

    // Create tooltip HTML structure matching Figma design exactly
    const isCompleted = eventProps.status === 'completed';

    // Escape HTML to prevent XSS attacks
    const escapedAptId = this.escapeHtml(eventProps.appointmentId || '');
    const escapedAptNumber = this.escapeHtml(eventProps.aptNumber || 'Unknown');
    const escapedCustomerName = this.escapeHtml(eventProps.customerName || 'Customer Name');
    const escapedPackageName = this.escapeHtml(eventProps.packageName || 'Package Name');
    const escapedVehicleYear = this.escapeHtml(eventProps.vehicleYear || 'N/A');
    const escapedVehicleMake = this.escapeHtml(eventProps.vehicleMake || 'Vehicle Make');
    const escapedVehicleModel = this.escapeHtml(eventProps.vehicleModel || 'Model');
    const escapedEmployeeName = this.escapeHtml(eventProps.employeeName || 'Employee Name');

    tooltip.innerHTML = `
      <div class="tooltip-header">
                 <div class="appointment-title-section">
           <span class="appointment-title clickable-appointment-title" data-apt-id="${escapedAptId}"> ${escapedAptNumber}: ${escapedCustomerName}</span>
           <div class="status-icon"></div>
         </div>
      </div>
      
      <div class="tooltip-content">
        <div class="info-row">
          <div class="info-icon">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 3.82048V7.15381L9 9.15381M1 7.15381C1 7.94174 1.15519 8.72196 1.45672 9.44991C1.75825 10.1779 2.20021 10.8393 2.75736 11.3964C3.31451 11.9536 3.97595 12.3956 4.7039 12.6971C5.43185 12.9986 6.21207 13.1538 7 13.1538C7.78793 13.1538 8.56815 12.9986 9.2961 12.6971C10.0241 12.3956 10.6855 11.9536 11.2426 11.3964C11.7998 10.8393 12.2417 10.1779 12.5433 9.44991C12.8448 8.72196 13 7.94174 13 7.15381C13 5.56251 12.3679 4.03639 11.2426 2.91117C10.1174 1.78595 8.5913 1.15381 7 1.15381C5.4087 1.15381 3.88258 1.78595 2.75736 2.91117C1.63214 4.03639 1 5.56251 1 7.15381Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>

          </div>
          <span class="info-text">${formattedDate}  ${timeString}</span>
        </div>
        
        <div class="info-row">
          <div class="info-icon">
            <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.00065 2.48714H2.66732C2.3137 2.48714 1.97456 2.62762 1.72451 2.87767C1.47446 3.12771 1.33398 3.46685 1.33398 3.82048V11.8205C1.33398 12.1741 1.47446 12.5132 1.72451 12.7633C1.97456 13.0133 2.3137 13.1538 2.66732 13.1538H9.33398C9.68761 13.1538 10.0267 13.0133 10.2768 12.7633C10.5268 12.5132 10.6673 12.1741 10.6673 11.8205V3.82048C10.6673 3.46685 10.5268 3.12771 10.2768 2.87767C10.0267 2.62762 9.68761 2.48714 9.33398 2.48714H8.00065M4.00065 2.48714C4.00065 2.13352 4.14113 1.79438 4.39118 1.54433C4.64122 1.29428 4.98036 1.15381 5.33398 1.15381H6.66732C7.02094 1.15381 7.36008 1.29428 7.61013 1.54433C7.86018 1.79438 8.00065 2.13352 8.00065 2.48714M4.00065 2.48714C4.00065 2.84076 4.14113 3.1799 4.39118 3.42995C4.64122 3.68 4.98036 3.82048 5.33398 3.82048H6.66732C7.02094 3.82048 7.36008 3.68 7.61013 3.42995C7.86018 3.1799 8.00065 2.84076 8.00065 2.48714M4.00065 7.15381H8.00065M4.00065 9.82047H8.00065" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>

          </div>
          <span class="info-text">${escapedPackageName}</span>
        </div>
        
        <div class="info-row">
          <div class="info-icon">
            <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.33333 8.48714C2.33333 8.84076 2.47381 9.1799 2.72386 9.42995C2.97391 9.68 3.31304 9.82047 3.66667 9.82047C4.02029 9.82047 4.35943 9.68 4.60948 9.42995C4.85952 9.1799 5 8.84076 5 8.48714M2.33333 8.48714C2.33333 8.13352 2.47381 7.79438 2.72386 7.54433C2.97391 7.29428 3.31304 7.15381 3.66667 7.15381C4.02029 7.15381 4.35943 7.29428 4.60948 7.54433C4.85952 7.79438 5 8.13352 5 8.48714M2.33333 8.48714H1V4.48714M5 8.48714H9M9 8.48714C9 8.84076 9.14048 9.1799 9.39052 9.42995C9.64057 9.68 9.97971 9.82047 10.3333 9.82047C10.687 9.82047 11.0261 9.68 11.2761 9.42995C11.5262 9.1799 11.6667 8.84076 11.6667 8.48714M9 8.48714C9 8.13352 9.14048 7.79438 9.39052 7.54433C9.64057 7.29428 9.97971 7.15381 10.3333 7.15381C10.687 7.15381 11.0261 7.29428 11.2761 7.54433C11.5262 7.79438 11.6667 8.13352 11.6667 8.48714M11.6667 8.48714H13V5.82047C13 5.46685 12.8595 5.12771 12.6095 4.87767C12.3594 4.62762 12.0203 4.48714 11.6667 4.48714H11M1 4.48714L2.33333 1.15381H8.33333L11 4.48714M1 4.48714H11M7 4.48714V1.15381" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>

          </div>
          <span class="info-text">${escapedVehicleYear} ${escapedVehicleMake} ${escapedVehicleModel}</span>
        </div>
        
        <div class="info-row">
          <div class="info-icon">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.112 11.7198C3.27701 11.1706 3.61464 10.6893 4.07483 10.3471C4.53502 10.005 5.09323 9.82031 5.66667 9.82047H8.33333C8.9075 9.82028 9.46641 10.0054 9.92692 10.3483C10.3874 10.6913 10.725 11.1737 10.8893 11.7238M1 7.15381C1 7.94174 1.15519 8.72196 1.45672 9.44991C1.75825 10.1779 2.20021 10.8393 2.75736 11.3964C3.31451 11.9536 3.97595 12.3956 4.7039 12.6971C5.43185 12.9986 6.21207 13.1538 7 13.1538C7.78793 13.1538 8.56815 12.9986 9.2961 12.6971C10.0241 12.3956 10.6855 11.9536 11.2426 11.3964C11.7998 10.8393 12.2417 10.1779 12.5433 9.44991C12.8448 8.72196 13 7.94174 13 7.15381C13 6.36588 12.8448 5.58566 12.5433 4.85771C12.2417 4.12975 11.7998 3.46832 11.2426 2.91117C10.6855 2.35402 10.0241 1.91206 9.2961 1.61053C8.56815 1.309 7.78793 1.15381 7 1.15381C6.21207 1.15381 5.43185 1.309 4.7039 1.61053C3.97595 1.91206 3.31451 2.35402 2.75736 2.91117C2.20021 3.46832 1.75825 4.12975 1.45672 4.85771C1.15519 5.58566 1 6.36588 1 7.15381ZM5 5.82047C5 6.35091 5.21071 6.85962 5.58579 7.23469C5.96086 7.60976 6.46957 7.82047 7 7.82047C7.53043 7.82047 8.03914 7.60976 8.41421 7.23469C8.78929 6.85962 9 6.35091 9 5.82047C9 5.29004 8.78929 4.78133 8.41421 4.40626C8.03914 4.03119 7.53043 3.82048 7 3.82048C6.46957 3.82048 5.96086 4.03119 5.58579 4.40626C5.21071 4.78133 5 5.29004 5 5.82047Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>

          </div>
          <span class="info-text">${escapedEmployeeName}</span>
        </div>
      </div>
      
      ${
        !isCompleted && !this.isStaffUser
          ? `
      <div class="tooltip-actions">
        <button class="reschedule-btn" data-apt-id="${escapedAptNumber}">Reschedule</button>
        <div class="cancel-link" data-apt-id="${escapedAptNumber}">Cancel Appointment</div>
      </div>
      `
          : ''
      }
    `;

    // We'll append arrow and tooltip in the correct order after positioning
    // For now, just append tooltip
    tooltipContainer.appendChild(tooltip);

    // Add event listener for appointment title click
    const appointmentTitle = tooltip.querySelector('.clickable-appointment-title') as HTMLElement;
    if (appointmentTitle) {
      appointmentTitle.style.cursor = 'pointer';
      appointmentTitle.addEventListener('click', e => {
        e.stopPropagation();
        this.onAppointmentTitleClick(eventProps.appointmentId || eventProps.aptNumber || 'Unknown');
      });
    }

    // Add event listeners for action buttons if not completed and not staff
    if (!isCompleted && !this.isStaffUser) {
      const rescheduleBtn = tooltip.querySelector('.reschedule-btn') as HTMLElement;
      const cancelLink = tooltip.querySelector('.cancel-link') as HTMLElement;

      if (rescheduleBtn) {
        rescheduleBtn.addEventListener('click', e => {
          e.stopPropagation();
          this.onRescheduleAppointment(eventProps.aptNumber || 'Unknown');
        });
      }

      if (cancelLink) {
        cancelLink.addEventListener('click', e => {
          e.stopPropagation();
          this.onCancelAppointment(eventProps.aptNumber || 'Unknown');
        });
      }
    }

    // Add mouse event listeners to keep tooltip visible when hovered
    tooltipContainer.addEventListener('mouseenter', () => {
      if (this.tooltipHideTimeout) {
        clearTimeout(this.tooltipHideTimeout);
        this.tooltipHideTimeout = null;
      }
    });

    tooltipContainer.addEventListener('mouseleave', () => {
      this.tooltipHideTimeout = setTimeout(() => {
        this.hideEventTooltip();
      }, 350);
    });

    // Add to DOM
    document.body.appendChild(tooltipContainer);

    // Position tooltip after a brief delay to ensure DOM is rendered
    setTimeout(() => {
      this.positionTooltip(tooltipContainer, element, arrow);
    }, 0);
  }

  /**
   * Position the appointment tooltip container and arrow relative to a target element, keeping it within the viewport and flipping the arrow when needed.
   * @example
   * positionTooltip(tooltipContainer, targetElement, arrow)
   * undefined
   * @param {{HTMLElement}} {{tooltipContainer}} - The container element that wraps the tooltip and arrow.
   * @param {{HTMLElement}} {{targetElement}} - The target element (event) to which the tooltip should be anchored.
   * @param {{HTMLElement}} {{arrow}} - The tooltip arrow element that will be positioned and flipped based on available space.
   * @returns {{void}} No return value.
   **/
  private positionTooltip(
    tooltipContainer: HTMLElement,
    targetElement: HTMLElement,
    arrow: HTMLElement
  ): void {
    const tooltip = tooltipContainer.querySelector('.appointment-tooltip') as HTMLElement;
    if (!tooltip) return;

    // Ensure target element still exists and is visible
    if (!targetElement.isConnected || !document.body.contains(targetElement)) {
      this.hideEventTooltip();
      return;
    }

    // Get target element position relative to viewport
    const targetRect = targetElement.getBoundingClientRect();

    // Reset tooltip position to measure properly
    tooltipContainer.style.left = '0px';
    tooltipContainer.style.top = '0px';
    tooltipContainer.style.visibility = 'hidden';

    // Force reflow to get accurate measurements
    void tooltipContainer.offsetHeight;

    const tooltipRect = tooltip.getBoundingClientRect();

    // Hide during positioning
    tooltipContainer.style.visibility = 'visible';

    // Calculate horizontal position (center tooltip on target)
    let leftPos = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;

    // Ensure tooltip doesn't go off screen horizontally
    const margin = 10;
    if (leftPos < margin) {
      leftPos = margin;
    } else if (leftPos + tooltipRect.width > window.innerWidth - margin) {
      leftPos = window.innerWidth - tooltipRect.width - margin;
    }

    // Calculate vertical position (below the target element by default)
    let topPos = targetRect.bottom + 2;
    let isFlipped = false;

    // Check if tooltip would go below viewport, if so position it above
    if (topPos + tooltipRect.height > window.innerHeight - margin) {
      topPos = targetRect.top - tooltipRect.height - 2;
      isFlipped = true;
    }

    // Ensure tooltip doesn't go above viewport (fallback)
    if (topPos < margin) {
      topPos = margin;
    }

    // Append arrow in correct order based on position
    if (isFlipped) {
      // Tooltip above event: Tooltip first, then arrow (arrow points down)
      tooltipContainer.appendChild(arrow);
      arrow.classList.add('flipped');
    } else {
      // Tooltip below event: Arrow first, then tooltip (arrow points up)
      tooltipContainer.insertBefore(arrow, tooltip);
      arrow.classList.remove('flipped');
    }

    // Apply final positioning
    tooltipContainer.style.position = 'fixed';
    tooltipContainer.style.left = `${Math.round(leftPos)}px`;
    tooltipContainer.style.top = `${Math.round(topPos)}px`;
    tooltipContainer.style.zIndex = '1050';
  }

  /**
   * Remove the event tooltip from the DOM and clear any pending tooltip hide timeout.
   * @example
   * hideEventTooltip()
   * // undefined
   * @returns {void} No return value.
   **/
  private hideEventTooltip(): void {
    // Clear any pending timeout
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }

    const tooltip = document.querySelector('.appointment-tooltip-container');
    if (tooltip) {
      tooltip.remove();
    }
  }

  private isTooltipTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }

    return !!target.closest('.appointment-tooltip-container');
  }

  /**
   * Returns an icon identifier for a given appointment status.
   * @example
   * etStatusIcon('confirmed')
   * tabler:circle-check-filled
   * @param {{string}} {{status}} - Status string ('confirmed', 'rescheduled', 'cancelled', 'pending', etc.).
   * @returns {{string}} Icon name corresponding to the status, or an empty string if none.
   **/
  getStatusIcon(status: string): string {
    switch (status) {
      case 'confirmed':
        return 'tabler:circle-check-filled';
      case 'rescheduled':
      case 'cancelled':
      case 'pending':
        return 'tabler:refresh';
      default:
        return '';
    }
  }

  // Date picker methods
  initializeDatePicker(): void {
    const currentYear = new Date().getFullYear();
    this.minDate = new Date(currentYear - 5, 0, 1);
    this.maxDate = new Date(currentYear + 5, 11, 31);
    this.configureDatePickerForView();
  }

  onHeaderClick(): void {
    this.configureDatePickerForView();
    this.showDatePicker = true;
  }

  /**
   * Configure the date picker view, selection mode, and selected date(s) based on the component's currentView and currentDate.
   * @example
   * onfigureDatePickerForView()
   * // Sets datePickerView to 'month'|'date', selectionMode to 'single'|'range', and selectedDate to a Date or [Date, Date]
   * @param {{void}} {{none}} - This method accepts no arguments and relies on this.currentView and this.currentDate.
   * @returns {{void}} Updates component state (datePickerView, selectionMode, selectedDate) and returns nothing.
   **/
  configureDatePickerForView(): void {
    switch (this.currentView) {
      case 'month':
        this.datePickerView = 'month';
        this.selectionMode = 'single';
        this.selectedDate = new Date(
          this.currentDate.getFullYear(),
          this.currentDate.getMonth(),
          1
        );
        break;
      case 'week': {
        this.datePickerView = 'date';
        this.selectionMode = 'range';
        const startOfWeek = this.getStartOfWeek(this.currentDate);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        this.selectedDate = [new Date(startOfWeek), new Date(endOfWeek)];
        break;
      }
      case 'day':
        this.datePickerView = 'date';
        this.selectionMode = 'single';
        this.selectedDate = new Date(this.currentDate);
        break;
    }
  }

  /**
   * Handle a date or date-range selection from the calendar, update component state (currentDate, selectedDate), trigger view updates and reload appointments as needed.
   * @example
   * nDateSelect(new Date())
   * undefined
   * @param {{any}} {{event}} - The selected date or date-range from the calendar (Date or [Date, Date]).
   * @returns {{void}} No return value; updates component state and triggers side effects (view update, appointment reload, hide picker).
   **/
  onDateSelect(event: any): void {
    const selectedDate = event;

    if (!selectedDate) return;

    // Handle click selection visual feedback
    setTimeout(() => {
      this.handlePrimeNgCalendarClick(event);
    }, 50);

    switch (this.currentView) {
      case 'month':
        if (selectedDate instanceof Date) {
          this.currentDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
          this.updateCalendarDate();
        }
        break;
      case 'week':
        if (Array.isArray(selectedDate) && selectedDate.length === 2) {
          // Use the first date of the selected range to navigate to that week
          this.currentDate = new Date(selectedDate[0]);
          this.updateCalendarDate();
        } else if (selectedDate instanceof Date) {
          // If a single date is selected, calculate the week range
          const startOfWeek = this.getStartOfWeek(selectedDate);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          this.selectedDate = [new Date(startOfWeek), new Date(endOfWeek)];
          this.currentDate = new Date(selectedDate);
          this.updateCalendarDate();
        }
        break;
      case 'day':
        if (selectedDate instanceof Date) {
          this.currentDate = new Date(selectedDate);
          this.updateCalendarDate();
        }
        break;
    }

    this.showDatePicker = false;
  }

  onDatePickerHide(): void {
    this.showDatePicker = false;
  }

  // Handle PrimeNG calendar cell click to add/remove selected class
  /**
   * Handle click events from a PrimeNG calendar and mark the corresponding calendar cell as selected.
   * @example
   * handlePrimeNgCalendarClick(new Date())
   * undefined
   * @param {{any}} {{event}} - The clicked value or event from the PrimeNG calendar (typically a Date instance) used to find and highlight the matching day/month cell.
   * @returns {{void}} Returns nothing.
   **/
  private handlePrimeNgCalendarClick(event: any): void {
    // Remove selected class from all calendar cells
    const allCells = document.querySelectorAll(
      '.p-datepicker .p-ripple, .p-monthpicker-month, .p-yearpicker-year'
    );
    allCells.forEach(cell => cell.classList.remove('selected'));

    // Find the clicked cell and add selected class
    // This is a bit tricky since PrimeNG doesn't expose the clicked element directly
    // We'll use the event data to find the corresponding cell
    if (event instanceof Date) {
      const dateValue = event.getDate();
      const monthValue = event.getMonth();

      // For date picker (day view)
      if (this.datePickerView === 'date') {
        const dayCells = document.querySelectorAll('.p-datepicker-calendar td .p-ripple');
        dayCells.forEach(cell => {
          const cellText = cell.textContent?.trim();
          if (cellText === dateValue.toString()) {
            // Additional check to ensure it's the correct month
            const isOtherMonth = cell.classList.contains('p-datepicker-other-month');
            if (!isOtherMonth) {
              cell.classList.add('selected');
            }
          }
        });
      }
      // For month picker
      else if (this.datePickerView === 'month') {
        const monthCells = document.querySelectorAll('.p-monthpicker-month');
        monthCells.forEach((cell, index) => {
          if (index === monthValue) {
            cell.classList.add('selected');
          }
        });
      }
    }
  }

  /**
   * Handle document click events to close the date picker, collapse expanded day overlays, and clear drag state.
   * @example
   * onDocumentClick(new MouseEvent('click'))
   * void
   * @param {{Event}} {{event}} - The DOM click event triggered on the document.
   * @returns {{void}} No return value.
   **/
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Handle date picker clicks
    if (this.showDatePicker && this.headerElement) {
      const target = event.target as Node;
      const headerEl = this.headerElement.nativeElement;

      // Check if dropdown element exists before accessing it
      if (this.datePickerDropdown) {
        const dropdownEl = this.datePickerDropdown.nativeElement;

        // Close dropdown if clicked outside of header or dropdown
        if (!headerEl.contains(target) && !dropdownEl.contains(target)) {
          this.showDatePicker = false;
        }
      } else if (!headerEl.contains(target)) {
        this.showDatePicker = false;
      }
    }

    // Handle expanded day overlay clicks
    const target = event.target as Element;
    const isExpandedOverlay = target.closest('.fc-daygrid-day-events.expanded');
    const isCalendarEvent = target.closest('.fc-event');

    // If clicked outside of expanded overlay and not on a calendar event, close all expanded days
    if (!isExpandedOverlay && !isCalendarEvent) {
      this.expandedDays.clear();
      this.refreshCalendarEvents();
    }

    // Clear drag state if clicking outside during drag
    if (this.isDragging) {
      this.isDragging = false;
      this.draggedEvent = null;
      this.hideDragTooltip();
      document.removeEventListener('mousemove', this.handleDragMouseMove.bind(this));
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    // Clear drag state if pressing escape during drag
    if (event.key === 'Escape' && this.isDragging) {
      this.isDragging = false;
      this.draggedEvent = null;
      this.hideDragTooltip();
      document.removeEventListener('mousemove', this.handleDragMouseMove.bind(this));
    }
  }

  // Custom week selection for week view
  /**
   * Generate an array of 7-day week ranges that cover the month of the given Date.
   * @example
   * etWeekRanges(new Date(2026, 0, 1))
   * [{ start: new Date(2025, 11, 28), end: new Date(2026, 0, 3) }, { start: new Date(2026, 0, 4), end: new Date(2026, 0, 10) }, ...]
   * @param {{Date}} {{month}} - Date contained within the target month; weeks returned cover the whole month.
   * @returns {{ { start: Date; end: Date; }[] }} Array of objects each with a start and end Date representing inclusive week ranges (7 days) aligned using this.getStartOfWeek.
   **/
  getWeekRanges(month: Date): { start: Date; end: Date }[] {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDayEnd = new Date(year, monthIndex + 1, 0).getTime();
    const weeks: { start: Date; end: Date }[] = [];
    let currentWeekStart = this.getStartOfWeek(firstDay);
    while (currentWeekStart.getTime() <= lastDayEnd) {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(currentWeekStart.getDate() + 6);
      weeks.push({
        start: new Date(currentWeekStart),
        end: new Date(weekEnd),
      });
      currentWeekStart = new Date(currentWeekStart);
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    }
    return weeks;
  }

  onRescheduleAppointment(aptNumber: string): void {
    // Find the appointment in the appointments array
    const appointment = this.findAppointmentByAptNumber(aptNumber);
    if (appointment) {
      this.appointmentToReschedule = appointment;
      this.isRescheduleModalVisible = true;
    }
    this.hideEventTooltip();
  }

  onCancelAppointment(aptNumber: string): void {
    // Find the appointment in the appointments array
    const appointment = this.findAppointmentByAptNumber(aptNumber);
    if (appointment) {
      this.appointmentToCancel = appointment;
      this.isCancelModalVisible = true;
    }
    this.hideEventTooltip();
  }

  /**
   * Handle click on an appointment title: hide tooltip, locate the appointment by ID or code, load latest details and open the details modal.
   * @example
   * nAppointmentTitleClick('APT12345')
   * void
   * @param {{string}} {{appointmentIdOrCode}} - Appointment identifier or code (UUID or appointment number) used to find and load appointment details.
   * @returns {{void}} No return value; opens the appointment details modal or logs a warning/error on failure.
   **/
  onAppointmentTitleClick(appointmentIdOrCode: string): void {
    this.hideEventTooltip();

    // Try to find appointment by ID first, then by code
    let appointment: Appointment | null = null;

    // Check if it's an ID (UUID format) or code
    if (appointmentIdOrCode.length > 10) {
      // Likely an ID, find by ID
      appointment = this.originalAppointments.find(apt => apt.id === appointmentIdOrCode) || null;
    }

    // If not found by ID, try by appointment code
    if (!appointment) {
      appointment = this.findAppointmentByAptNumber(appointmentIdOrCode);
    }

    if (appointment) {
      // Load full appointment details by ID to ensure we have the latest data
      this.appointmentService.getAppointmentById(appointment.id).subscribe({
        next: response => {
          this.selectedAppointmentForModal = {
            ...appointment,
            ...response.data,
            appointment_time_local:
              response.data?.appointment_time_local ?? appointment?.appointment_time_local,
          };
          this.isAppointmentDetailsModalVisible = true;
          this.cdr.markForCheck();
        },
        error: error => {
          console.error('❌ Error loading appointment:', error);
          // Fallback to using the appointment we found locally
          this.selectedAppointmentForModal = appointment;
          this.isAppointmentDetailsModalVisible = true;
          this.cdr.markForCheck();
        },
      });
    } else {
      console.warn('⚠️ Appointment not found:', appointmentIdOrCode);
    }
  }

  closeAppointmentDetailsModal(): void {
    this.isAppointmentDetailsModalVisible = false;
    this.selectedAppointmentForModal = null;
  }

  onAppointmentUpdated(): void {
    // Reload appointments when an appointment is updated
    this.loadAppointments();
  }

  onRescheduleFromDetailsModal(appointment: Appointment): void {
    this.closeAppointmentDetailsModal();
    this.appointmentToReschedule = appointment;
    this.isRescheduleModalVisible = true;
  }

  onCancelFromDetailsModal(appointment: Appointment): void {
    this.closeAppointmentDetailsModal();
    this.appointmentToCancel = appointment;
    this.isCancelModalVisible = true;
  }

  /**
   * Mark the given appointment as completed by sending an update request, show success or error notifications, and close the details modal on success.
   * @example
   * nMarkCompleteFromDetailsModal({ id: '12345' })
   * undefined
   * @param {{Appointment}} {{appointment}} - Appointment object to mark as completed; must include an id property.
   * @returns {{void}} No return value; performs an asynchronous update and displays notifications based on the result.
   **/
  onMarkCompleteFromDetailsModal(appointment: Appointment): void {
    if (!appointment?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to mark complete. Appointment data is missing.',
      });
      return;
    }

    const payload = {
      appointment_status: 'completed',
    };

    this.appointmentService.updateAppointment(appointment.id, payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Appointment complete!',
          detail: 'The appointment has been marked complete successfully.',
        });
        this.closeAppointmentDetailsModal();
        this.onAppointmentUpdated();
        this.cdr.markForCheck();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to mark appointment complete. Please try again.',
        });
        this.cdr.markForCheck();
      },
    });
  }

  private findAppointmentByAptNumber(aptNumber: string): Appointment | null {
    // Find the appointment from this.originalAppointments that matches the aptNumber
    return this.originalAppointments.find(apt => apt.appointment_code === aptNumber) || null;
  }

  // Reschedule Modal Event Handlers
  onRescheduleModalClose(): void {
    this.isRescheduleModalVisible = false;
    this.appointmentToReschedule = null;
    this.droppedDate = null;
  }

  onRescheduleUpdate(event: { appointmentId: string; payload: any }): void {
    runRescheduleUpdate(event, this.appointmentService, this.messageService, {
      setLoading: v => {
        this.isRescheduleLoading = v;
        this.cdr.markForCheck();
      },
      closeRescheduleModal: () => {
        this.isRescheduleModalVisible = false;
        this.appointmentToReschedule = null;
        this.droppedDate = null;
        this.cdr.markForCheck();
      },
      loadAppointments: () => this.loadAppointments(),
    });
  }

  // Cancel Modal Event Handlers
  onCancelConfirm(appointment: Appointment): void {
    this.isCancelModalVisible = false;
    this.isCancellationReasonModalVisible = true;
  }

  onCancelModalClose(): void {
    this.isCancelModalVisible = false;
    this.appointmentToCancel = null;
  }

  /**
   * Submit a cancellation reason and cancel the given appointment, updating component state and showing success/error modals.
   * @example
   * nCancelReasonSubmit({ item: { id: 123, /* Appointment fields */
  onCancelReasonSubmit(data: { item: Appointment | any; reason: string }): void {
    const payload = {
      appointment_status: 'cancelled',
      cancellation_reason: data.reason,
    };

    this.appointmentService.cancelAppointment(data.item.id, payload).subscribe({
      next: response => {
        this.isCancellationReasonModalVisible = false;
        this.isCancellationSuccessModalVisible = true;
        // Reload appointments to reflect the change
        this.loadAppointments();
        this.cdr.markForCheck();
      },
      error: error => {
        console.error('❌ Error cancelling appointment:', error);
        this.isCancellationReasonModalVisible = false;
        this.isCancellationErrorModalVisible = true;
        this.appointmentToCancel = null;
        this.cdr.markForCheck();
      },
    });
  }

  onCancellationReasonModalClose(): void {
    this.isCancellationReasonModalVisible = false;
    this.appointmentToCancel = null;
  }

  onCancellationSuccessModalOkay(): void {
    this.isCancellationSuccessModalVisible = false;
    this.appointmentToCancel = null;
    // Refresh appointments after cancellation
    this.loadAppointments();
  }

  onCancellationSuccessModalCreateAnother(): void {
    this.isCancellationSuccessModalVisible = false;
    this.appointmentToCancel = null;
    // Emit event to parent component to handle appointment creation
    this.createAnotherAppointment.emit();
  }

  onCancellationSuccessModalClose(): void {
    this.isCancellationSuccessModalVisible = false;
    this.appointmentToCancel = null;
  }

  /**
   * Clean up tooltips, timeouts, mousemove listener, and location subscription when the component is destroyed.
   * @example
   * gOnDestroy()
   * // void
   * @returns {void} Cleans up timers, event listeners, and subscriptions to prevent memory leaks.
   */
  ngOnDestroy(): void {
    // Clean up tooltip timeout to prevent memory leaks
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }
    this.hideEventTooltip();

    // Clean up drag tooltip timeout
    if (this.dragTooltipHideTimeout) {
      clearTimeout(this.dragTooltipHideTimeout);
      this.dragTooltipHideTimeout = null;
    }
    this.hideDragTooltip();

    // Remove mouse move listener
    document.removeEventListener('mousemove', this.handleDragMouseMove.bind(this));

    // Clean up location subscription
    this.locationsSubscription.unsubscribe();
  }

  // Debug method to test expansion for a specific date
  testExpansion(dateStr: string): void {
    this.toggleDayExpansion(dateStr);
  }

  /**
   * Render HTML content for a calendar event based on view type, overflow state, expanded day, and available time.
   * @example
   * renderEventContent({ event: { title: 'Apt #123', start: new Date(), extendedProps: { isOverflow: false, appointmentTime: '10:00', duration: 30 } } })
   * { html: '<div class="fc-event-main"><div class="fc-event-time">10:00 - 10:30</div><div class="fc-event-title time-move">Apt #123</div></div>' }
   * @param {{any}} {{arg}} - Argument provided by the calendar library containing the event object and view context.
   * @returns {{any}} Object with an html property containing the rendered HTML string for the event.
   **/
  private renderEventContent(arg: any): any {
    const { event } = arg;
    const viewType = this.calendarComponent?.getApi()?.view.type;
    const isTimeView =
      viewType === 'timeGridDay' || viewType === 'timeGridWeek' || viewType === 'timeGridWorkWeek';
    const isMonthView = viewType === 'dayGridMonth';
    const { isOverflow } = event.extendedProps;

    // Check if this event is in an expanded day
    const eventDate = event.start ? this.formatDateAsLocalKey(event.start) : event.date;
    const isInExpandedDay = this.isDayExpanded(eventDate);

    // For overflow events, show special styling
    if (isOverflow) {
      return {
        html: `
          <div class="fc-event-main">
            <div class="fc-event-title overflow-title">${event.title}</div>
          </div>
        `,
      };
    }

    // For month view or expanded days, show only the appointment number
    if (isMonthView || isInExpandedDay) {
      return {
        html: `
          <div class="fc-event-main">
            <div class="fc-event-title">${event.title}</div>
          </div>
        `,
      };
    }

    // For week and day views, show time and appointment number
    if (isTimeView) {
      const appointmentNumber = event.title;

      // Calculate time range using same logic as tooltip
      const timeRange = this.calculateTimeRange(
        event.extendedProps.appointmentTime,
        event.extendedProps.duration
      );

      // Always show time for week and day views if it exists
      if (timeRange && timeRange.trim() !== '' && timeRange !== 'Time not available') {
        return {
          html: `
            <div class="fc-event-main">
              <div class="fc-event-time">${timeRange}</div>
              <div class="fc-event-title time-move">${appointmentNumber}</div>
            </div>
          `,
        };
      }
      return {
        html: `
          <div class="fc-event-main">
            <div class="fc-event-title">${appointmentNumber}</div>
          </div>
        `,
      };
    }

    // Fallback for other views
    return {
      html: `
        <div class="fc-event-main">
          <div class="fc-event-title">${event.title}</div>
        </div>
      `,
    };
  }

  // Method to calculate time range from appointmentTime and duration (same logic as tooltip)
  /**
   * Calculate a human-readable time range string for an appointment given a start time and duration.
   * @example
   * calculateTimeRange('2026-03-17T09:00:00Z', { hours: 1, minutes: 30 })
   * "9:00 AM - 10:30 AM"
   * @param {{string}} {{appointmentTime}} - ISO 8601 date-time string representing the appointment start.
   * @param {{{ minutes?: number; hours?: number }}} {{duration}} - Duration object with optional hours and/or minutes.
   * @returns {{string}} Returns a formatted time range like "9:00 AM - 10:30 AM", or "Time not available" if inputs are missing.
   **/
  private calculateTimeRange(
    appointmentTime: string,
    duration: { minutes?: number; hours?: number }
  ): string {
    if (!appointmentTime || !duration) {
      return 'Time not available';
    }

    const startTime = new Date(appointmentTime);
    const durationMinutes = (duration.hours || 0) * 60 + (duration.minutes || 0);
    const endTime = new Date(startTime.getTime() + durationMinutes * 60000);

    const formatTime = (date: Date) => {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  }

  // Method to handle day cell mounting and add expanded class
  private handleDayCellMount(args: any): void {
    const dateStr = this.formatDateAsLocalKey(args.date);
    const eventsContainer = args.el.querySelector('.fc-daygrid-day-events');

    if (eventsContainer && this.isDayExpanded(dateStr)) {
      eventsContainer.classList.add('expanded');
    }
  }

  // Drag and drop event handlers
  /**
   * Handle the start of an event drag: validate permissions/status, update UI state, and initialize drag tooltip and listeners.
   * @example
   * andleEventDragStart({ event: { extendedProps: { status: 'pending', isOverflow: false } }, jsEvent: MouseEvent, el: HTMLElement })
   * undefined
   * @param {any} dragInfo - Drag information object containing event, jsEvent, and el used to control drag behavior.
   * @returns {void} No return value; performs side effects to begin dragging (prevent default when disallowed, update UI, show tooltip, add listeners).
   **/
  handleEventDragStart(dragInfo: any): void {
    const { event } = dragInfo;
    const eventProps = event.extendedProps;
    if (!this.isAdminUser) {
      dragInfo.jsEvent.preventDefault();
      return;
    }

    // Don't allow dragging overflow events
    if (eventProps.isOverflow) {
      dragInfo.jsEvent.preventDefault();
      return;
    }

    // Don't allow dragging completed or cancelled appointments
    if (eventProps.status === 'completed' || eventProps.status === 'cancelled') {
      dragInfo.jsEvent.preventDefault();
      return;
    }

    // Close any open tooltips when dragging starts
    this.hideEventTooltip();

    // Close any expanded day overlays when dragging starts
    this.expandedDays.clear();
    this.refreshCalendarEvents();

    // Add dragging class for visual feedback
    const eventElement = dragInfo.el;
    if (eventElement) {
      eventElement.classList.add('dragging');
    }

    // Set drag state and show drag tooltip
    this.isDragging = true;
    this.draggedEvent = event;
    this.showDragTooltip(eventProps);

    // Add mouse move listener for drag tooltip
    document.addEventListener('mousemove', this.handleDragMouseMove.bind(this));
  }

  /**
   * Handle stopping of an event drag: removes dragging class, clears drag state, hides the drag tooltip, and removes the mousemove listener.
   * @example
   * andleEventDragStop({ event: someEvent, el: someHTMLElement })
   * undefined
   * @param {{any}} {{dragInfo}} - Drag information object provided by FullCalendar containing the event and element (el) references.
   * @returns {{void}} Nothing is returned.
   */
  handleEventDragStop(dragInfo: any): void {
    // Remove dragging class
    const eventElement = dragInfo.el;
    if (eventElement) {
      eventElement.classList.remove('dragging');
    }

    // Clear drag state and hide drag tooltip
    this.isDragging = false;
    this.draggedEvent = null;
    this.hideDragTooltip();

    // Remove mouse move listener
    document.removeEventListener('mousemove', this.handleDragMouseMove.bind(this));
  }

  /**
   * Show a temporary drag tooltip near the cursor when dragging an appointment.
   * @example
   * showDragTooltip({ time: '10:00 AM' })
   * undefined
   * @param {{any}} {{eventProps}} - Event properties object that may include a 'time' string used in the tooltip.
   * @returns {{void}} No return value; the tooltip is created and positioned asynchronously.
   **/
  private showDragTooltip(eventProps: any): void {
    // Clear any pending hide timeout
    if (this.dragTooltipHideTimeout) {
      clearTimeout(this.dragTooltipHideTimeout);
      this.dragTooltipHideTimeout = null;
    }

    // Remove existing drag tooltip
    this.hideDragTooltip();

    // Create drag tooltip container
    const tooltipContainer = document.createElement('div');
    tooltipContainer.className = 'drag-tooltip-container';
    tooltipContainer.style.position = 'fixed';
    tooltipContainer.style.left = '-9999px';
    tooltipContainer.style.top = '-9999px';
    tooltipContainer.style.zIndex = '1060';

    // Create drag tooltip content
    const tooltip = document.createElement('div');
    tooltip.className = 'drag-tooltip';

    // Format time duration
    const timeString = eventProps.time || 'Time not available';

    // Escape HTML to prevent XSS attacks
    const escapedTimeString = this.escapeHtml(timeString);

    tooltip.innerHTML = `
      <div class="drag-tooltip-content">
        ${escapedTimeString}
      </div>
    `;

    tooltipContainer.appendChild(tooltip);
    document.body.appendChild(tooltipContainer);

    // Position tooltip after a brief delay
    setTimeout(() => {
      this.positionDragTooltip(tooltipContainer);
    }, 10);
  }

  /**
   * Position the drag tooltip element near the last known mouse coordinates, constraining it to remain within the viewport.
   * @example
   * positionDragTooltip(tooltipContainer)
   * undefined
   * @param {{HTMLElement}} {{tooltipContainer}} - Tooltip container element that wraps the `.drag-tooltip` element.
   * @returns {{void}} No return value.
   **/
  private positionDragTooltip(tooltipContainer: HTMLElement): void {
    const tooltip = tooltipContainer.querySelector('.drag-tooltip') as HTMLElement;
    if (!tooltip) return;

    // Get mouse position (we'll update this in the mouse move handler)
    const mouseX = this.lastMouseX || 0;
    const mouseY = this.lastMouseY || 0;

    // Reset tooltip position to measure properly
    tooltipContainer.style.left = '0px';
    tooltipContainer.style.top = '0px';
    tooltipContainer.style.visibility = 'hidden';

    // Force reflow
    void tooltipContainer.offsetHeight;

    const tooltipRect = tooltip.getBoundingClientRect();
    tooltipContainer.style.visibility = 'visible';

    // Position tooltip below and slightly to the right of mouse cursor
    const margin = 10;
    let leftPos = mouseX + 15;
    let topPos = mouseY + 15;

    // Ensure tooltip doesn't go off screen
    if (leftPos + tooltipRect.width > window.innerWidth - margin) {
      leftPos = mouseX - tooltipRect.width - 15;
    }

    if (topPos + tooltipRect.height > window.innerHeight - margin) {
      topPos = mouseY - tooltipRect.height - 15;
    }

    tooltipContainer.style.position = 'fixed';
    tooltipContainer.style.left = `${Math.round(leftPos)}px`;
    tooltipContainer.style.top = `${Math.round(topPos)}px`;
  }

  /**
   * Handle mousemove events during a drag operation, update last mouse coordinates and reposition the drag tooltip if present.
   * @example
   * handleDragMouseMove(new MouseEvent('mousemove'))
   * undefined
   * @param {{MouseEvent}} {{event}} - Mouse event triggered while dragging.
   * @returns {{void}} No value returned.
   **/
  private handleDragMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    // Store mouse position
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;

    // Update drag tooltip position
    const tooltipContainer = document.querySelector('.drag-tooltip-container') as HTMLElement;
    if (tooltipContainer) {
      this.positionDragTooltip(tooltipContainer);
    }
  }

  /**
   * Hide and remove the drag tooltip container from the DOM and clear any pending hide timeout.
   * @example
   * hideDragTooltip()
   * // undefined
   * @returns {void} No return value.
   */
  private hideDragTooltip(): void {
    // Clear any pending hide timeout
    if (this.dragTooltipHideTimeout) {
      clearTimeout(this.dragTooltipHideTimeout);
      this.dragTooltipHideTimeout = null;
    }

    const tooltip = document.querySelector('.drag-tooltip-container');
    if (tooltip) {
      tooltip.remove();
    }
  }

  /**
   * Handle calendar event drop to initiate rescheduling while enforcing permissions and restrictions.
   * @example
   * andleEventDrop({ event: sampleEvent, revert: () => {} })
   * undefined
   * @param {{any}} {{dropInfo}} - The drop information object from the calendar library containing the dragged event (dropInfo.event) and a revert() function.
   * @returns {{void}} Return undefined; the function triggers UI side-effects (permission checks, showing a reschedule modal, and reverting the event) and does not return a value.
   **/
  handleEventDrop(dropInfo: any): void {
    const { event } = dropInfo;
    const eventProps = event.extendedProps;
    const newDate = event.start;

    // Don't allow dropping overflow events
    if (eventProps.isOverflow) {
      dropInfo.revert();
      return;
    }

    if (!this.isAdminUser) {
      dropInfo.revert();
      this.messageService.add({
        severity: 'error',
        summary: 'No edits possible!',
        detail: 'You do not have permission to edit the appointment.',
      });
      return;
    }

    // Don't allow dropping completed or cancelled appointments
    if (eventProps.status === 'completed' || eventProps.status === 'cancelled') {
      dropInfo.revert();
      return;
    }

    // Find the appointment to reschedule
    const appointment = this.findAppointmentByAptNumber(eventProps.aptNumber);
    if (appointment) {
      this.appointmentToReschedule = appointment;
      // Pass the dropped date to the reschedule modal
      this.droppedDate = newDate;
      this.isRescheduleModalVisible = true;
    }

    // Revert the drop to keep the original position until confirmed
    dropInfo.revert();
  }

  // Handle event drop revert (when drop is cancelled)
  handleEventDropRevert(dropInfo: any): void {
    // Clear drag state and hide drag tooltip when drop is reverted
    this.isDragging = false;
    this.draggedEvent = null;
    this.hideDragTooltip();

    // Remove mouse move listener
    document.removeEventListener('mousemove', this.handleDragMouseMove.bind(this));
  }

  // Search filtering methods
  private applySearchFilter(): void {
    // Always use original appointments and let the processEventsForDisplay handle the opacity
    this.appointments = this.transformAppointments(this.originalAppointments);

    // Refresh calendar events to update the display
    if (this.calendarComponent && this.calendarComponent.getApi()) {
      this.refreshCalendarEvents();
    }
  }

  /**
   * Checks whether the given appointment contains the search term in any searchable text field.
   * @example
   * matchesSearchTerm(sampleAppointment, 'john')
   * true
   * @param {{Appointment}} {{appointment}} - Appointment object to search within.
   * @param {{string}} {{searchTerm}} - Search term (case-insensitive, trimmed) to match against appointment fields.
   * @returns {{boolean}} True if the search term is found in any of the appointment's searchable fields, otherwise false.
   **/
  private matchesSearchTerm(appointment: Appointment, searchTerm: string): boolean {
    const term = searchTerm.toLowerCase().trim();
    return Boolean(
      appointment.appointment_code?.toLowerCase().includes(term) ||
      appointment.customer_name?.toLowerCase().includes(term) ||
      appointment.package_name?.toLowerCase().includes(term) ||
      appointment.vehicle_make?.toLowerCase().includes(term) ||
      appointment.vehicle_model?.toLowerCase().includes(term) ||
      appointment.employee_name?.toLowerCase().includes(term)
    );
  }

  /**
   * Determine whether an event's searchable fields contain the provided search term (case-insensitive).
   * @example
   * matchesSearchTermForEvent(sampleEvent, 'john')
   * true
   * @param {{EventInput}} {{event}} - Event object whose extendedProps will be searched for the term.
   * @param {{string}} {{searchTerm}} - Search term to match against event fields; it will be lowercased and trimmed.
   * @returns {{boolean}} True if the search term is found in aptNumber, customerName, packageName, vehicleMake, vehicleModel, or employeeName; otherwise false.
   **/
  private matchesSearchTermForEvent(event: EventInput, searchTerm: string): boolean {
    const term = searchTerm.toLowerCase().trim();
    const eventProps = event.extendedProps as any;
    return Boolean(
      eventProps?.aptNumber?.toLowerCase().includes(term) ||
      eventProps?.customerName?.toLowerCase().includes(term) ||
      eventProps?.packageName?.toLowerCase().includes(term) ||
      eventProps?.vehicleMake?.toLowerCase().includes(term) ||
      eventProps?.vehicleModel?.toLowerCase().includes(term) ||
      eventProps?.employeeName?.toLowerCase().includes(term)
    );
  }

  formatDuration(duration: { minutes?: number; hours?: number }): string {
    return formatDuration(duration);
  }

  /**
   * Escape HTML to prevent XSS attacks when inserting user data into innerHTML
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Format a Date into local calendar day key (YYYY-MM-DD).
   * This avoids UTC conversion day shifts from toISOString().
   */
  private formatDateAsLocalKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
